import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { whenLobbyArrived } from './lobby-arrival.js';
import { AuthWaitBar } from './auth-wait-bar.js';
/**
 * First-run username picker. New accounts get a random adjective-noun suggestion
 * from the server (never the Google real name); this modal lets the player accept,
 * shuffle, or type their own before entering the game. Built with DOM APIs +
 * textContent (no innerHTML with user data).
 */

let UsernameOnboarding = {};

UsernameOnboarding = (function () {
  const ADJECTIVES = [
    'brave', 'cosmic', 'swift', 'lunar', 'golden', 'silent', 'crimson', 'azure',
    'mystic', 'noble', 'clever', 'wild', 'bright', 'frost', 'ember', 'shadow',
    'radiant', 'stellar', 'velvet', 'iron', 'jade', 'amber', 'onyx', 'coral',
  ];
  const NOUNS = [
    'otter', 'falcon', 'comet', 'tiger', 'raven', 'fox', 'wolf', 'lynx',
    'phoenix', 'drake', 'heron', 'panther', 'sparrow', 'badger', 'moth', 'koi',
    'stag', 'owl', 'bison', 'crane', 'viper', 'hare', 'orca', 'wren',
  ];

  function uiText(key, fallback) {
    const t = i18n && i18n.t ? i18n.t(key) : null;
    return t && t !== key ? t : fallback;
  }

  function randomSuggestion() {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    const num = Math.floor(Math.random() * 900) + 100;
    return `${adj}-${noun}-${num}`;
  }

  // Mirror the server's normalizeUsernameCandidate: 2–30 chars, no '@'.
  function isValid(name) {
    const v = String(name || '').trim();
    return v.length >= 2 && v.length <= 30 && !v.includes('@');
  }

  /**
   * Show the picker. Resolves once the player has saved a username (or the
   * suggestion, unchanged). Never rejects — a save failure keeps the modal open.
   *
   * `saveAfter`: what must land before the name is written. A new account's
   * card goes up while its consent is still being recorded (main.js), and no
   * name is written for an account whose consent is not on record: a save
   * waits for it, busy, and one it refuses writes nothing and stays busy (the
   * caller turns the login away).
   * @param {string} initialSuggestion server-provided default
   * @param {{ saveAfter?: Promise<unknown> | null }} [options]
   * @returns {Promise<string>} the chosen username
   */
  function show(initialSuggestion: string, { saveAfter = null }: { saveAfter?: Promise<unknown> | null } = {}): Promise<string> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'username-onboarding-overlay';
      overlay.id = 'username-onboarding-overlay';

      const card = document.createElement('div');
      card.className = 'username-onboarding-card';

      const title = document.createElement('h2');
      title.textContent = uiText('username.title', 'Choose your name');
      const sub = document.createElement('p');
      sub.className = 'username-onboarding-sub';
      sub.textContent = uiText('username.subtitle', 'This is how other players will see you.');

      const row = document.createElement('div');
      row.className = 'username-onboarding-row';
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'username-onboarding-input';
      input.maxLength = 30;
      input.value = isValid(initialSuggestion) ? initialSuggestion : randomSuggestion();
      input.setAttribute('aria-label', uiText('username.title', 'Choose your name'));

      const shuffle = document.createElement('button');
      shuffle.type = 'button';
      shuffle.className = 'username-onboarding-shuffle';
      shuffle.title = uiText('username.shuffle', 'Suggest another');
      shuffle.textContent = '🎲';

      row.appendChild(input);
      row.appendChild(shuffle);

      const error = document.createElement('div');
      error.className = 'username-onboarding-error';
      error.setAttribute('role', 'alert');

      const save = document.createElement('button');
      save.type = 'button';
      save.className = 'username-onboarding-save';
      save.textContent = uiText('username.save', 'Enter Moonfall');

      card.appendChild(title);
      card.appendChild(sub);
      card.appendChild(row);
      card.appendChild(error);
      card.appendChild(save);
      overlay.appendChild(card);
      document.body.appendChild(overlay);
      /* The card is the next surface: whatever wait led here (the consent
         gate's) ends with its bar, in the frame the card appears. */
      AuthWaitBar.disarmAll();
      input.focus();
      input.select();

      shuffle.addEventListener('click', () => {
        input.value = randomSuggestion();
        error.textContent = '';
        input.focus();
      });

      let saving = false;
      async function doSave() {
        if (saving) return;
        const name = input.value.trim();
        if (!isValid(name)) {
          error.textContent = uiText('username.invalid', 'Please use 2–30 characters (no @).');
          return;
        }
        saving = true;
        save.disabled = true;
        shuffle.disabled = true;
        /* From the press to the finished lobby: past the grace, one bar
           (ui/auth-wait-bar.js). */
        AuthWaitBar.arm(save);
        if (saveAfter) {
          try {
            await saveAfter;
          } catch (_) {
            // Nothing is written; the caller refuses the login.
            return;
          }
        }
        try {
          if (MoonfallCloud && MoonfallCloud.users && MoonfallCloud.users.updateProfile) {
            await MoonfallCloud.users.updateProfile(name);
          }
          /* HANDED OVER, NOT CLOSED. Resolving lets the lobby be built; the
             card stays up — busy — until that lobby has arrived whole, then
             fades away over it. Closing here showed the lobby assembling
             itself in front of the new player (13 pop-ins, measured). */
          save.setAttribute('aria-busy', 'true');
          save.classList.add('btn-pending');
          overlay.classList.add('is-entering');
          resolve(name);
          void whenLobbyArrived({ capMs: 8000 }).then(() => {
            // A bar on screen fades out with the card, not a frame before it.
            AuthWaitBar.settle(save);
            overlay.classList.add('is-leaving');
            setTimeout(() => overlay.remove(), 250);
          });
        } catch (err) {
          console.error('[UsernameOnboarding] save failed:', err);
          AuthWaitBar.disarm(save);
          error.textContent = uiText('username.saveError', 'Could not save. Please try again.');
          save.disabled = false;
          shuffle.disabled = false;
          saving = false;
        }
      }

      save.addEventListener('click', doSave);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSave(); });
    });
  }

  return { show, randomSuggestion, isValid };
})();

export { UsernameOnboarding };
